import axios from 'axios';
import config from '../utils/config';
import { fallbackBlogs as importedFallbackBlogs } from '../data/fallbackBlogs';
import { processBlogPost, processBlogPosts } from '../utils/blogUtils';
import {
  cacheBlogPost,
  getCachedBlogPost,
  cacheBlogList,
  getCachedBlogList,
  cacheCategoryBlogs,
  getCachedCategoryBlogs,
  cacheSearchResults,
  getCachedSearchResults,
  cacheCategories,
  getCachedCategories,
  cacheFeaturedBlogs,
  getCachedFeaturedBlogs,
  cacheRelatedBlogs,
  getCachedRelatedBlogs,
  invalidateBlogPost,
  invalidateBlogLists,
  invalidateSearchCaches
} from '../utils/cacheUtils';

// Ensure fallbackBlogs is always an array
const fallbackBlogs = importedFallbackBlogs || [];

const API_BASE_URL = config.api.baseURL;

// Create axios instance for blog API calls
const blogApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
blogApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Blog API Error:', error);
    return Promise.reject(error);
  }
);

// Helper function to merge API blogs with fallback blogs
const mergeBlogsWithFallback = (apiBlogs = []) => {
  console.log('📦 Merging API blogs with fallback blogs');
  console.log('   API blogs count:', apiBlogs.length);
  console.log('   Fallback blogs count:', fallbackBlogs.length);
  
  // Process API blogs to fix image URLs
  const processedApiBlogs = processBlogPosts(apiBlogs);
  
  // Combine processed API blogs with fallback blogs
  const merged = [...processedApiBlogs, ...fallbackBlogs];
  
  console.log('   Total merged blogs:', merged.length);
  return merged;
};

export const blogService = {
  // Get all published blog posts with pagination and filtering
  async getBlogs(params = {}) {
    console.log('🔵 blogService.getBlogs called with params:', params);
    console.log('🔵 API_BASE_URL:', API_BASE_URL);
    try {
      // Check cache first
      const cached = getCachedBlogList(params);
      if (cached) {
        console.log('✅ Returning cached blog list:', cached);
        return cached;
      }

      console.log('🌐 Making API request to /blog/posts');
      const response = await blogApi.get('/blog/posts', { params });
      console.log('✅ API response received:', response.status, response.data);
      const data = response.data;
      
      // Extract data from API response format
      const result = data.success ? data.data : data;
      console.log('📦 Extracted result:', result);
      
      // Cache the result
      cacheBlogList(params, result);
      
      return result;
    } catch (error) {
      console.error('❌ Error fetching blogs:', error);
      console.error('❌ Error details:', error.message, error.response?.status, error.response?.data);
      
      // Fall back to hardcoded blogs instead of throwing
      console.log('🔄 Falling back to hardcoded blog data');
      const fallbackResult = {
        posts: fallbackBlogs,
        total: fallbackBlogs.length,
        page: 1,
        limit: params.limit || 10,
        totalPages: 1,
      };
      return fallbackResult;
    }
  },

  // Get a single blog post by slug
  async getBlogBySlug(slug) {
    console.log('🔵 blogService.getBlogBySlug called with slug:', slug);
    try {
      // Check cache first
      const cached = getCachedBlogPost(slug);
      if (cached) {
        console.log('✅ Returning cached blog post');
        return cached;
      }

      // Check if it's a fallback blog first
      const fallbackBlog = fallbackBlogs.find(blog => blog.slug === slug);
      if (fallbackBlog) {
        console.log('✅ Found in fallback blogs:', fallbackBlog.title);
        cacheBlogPost(slug, fallbackBlog);
        return fallbackBlog;
      }

      // Try to fetch from API
      console.log('🌐 Making API request to /blog/posts/' + slug);
      const response = await blogApi.get(`/blog/posts/${slug}`);
      console.log('✅ API response received:', response.status, response.data);
      const data = response.data;
      
      // Extract data from API response format - handle potential nested structure
      let result = data.success ? data.data : data;
      
      // Check if result is nested (has success property inside)
      if (result && result.success !== undefined && result.data !== undefined) {
        console.log('📦 Detected nested response structure, extracting inner data');
        result = result.data;
      }
      
      console.log('📦 Extracted blog post:', result?.title || 'No title');
      
      // Process the blog post to fix image URLs
      const processedPost = processBlogPost(result);
      console.log('✅ Processed blog post with fixed image URLs');
      
      // Cache the processed result
      cacheBlogPost(slug, processedPost);
      
      return processedPost;
    } catch (error) {
      console.error('❌ Error fetching blog by slug:', error);
      console.error('❌ Error response:', error.response?.status, error.response?.data);
      
      // Check fallback blogs again as last resort
      const fallbackBlog = fallbackBlogs.find(blog => blog.slug === slug);
      if (fallbackBlog) {
        console.log('🔄 Returning fallback blog due to API error');
        return fallbackBlog;
      }
      
      throw error;
    }
  },

  // Get blogs by category
  async getBlogsByCategory(categorySlug, params = {}) {
    try {
      // Check cache first
      const cached = getCachedCategoryBlogs(categorySlug, params);
      if (cached) {
        return cached;
      }

      const response = await blogApi.get(`/blog/posts/category/${categorySlug}`, { params });
      const data = response.data;
      
      // Extract data from API response format
      const result = data.success ? data.data : data;
      
      // Cache the result
      cacheCategoryBlogs(categorySlug, params, result);
      
      return result;
    } catch (error) {
      console.error('Error fetching blogs by category:', error);
      throw error;
    }
  },

  // Get blogs by tag
  async getBlogsByTag(tag, params = {}) {
    try {
      // For tags, we use the same caching mechanism as categories
      const cached = getCachedCategoryBlogs(`tag_${tag}`, params);
      if (cached) {
        return cached;
      }

      const response = await blogApi.get(`/blog/posts/tag/${tag}`, { params });
      const data = response.data;
      
      // Extract data from API response format
      const result = data.success ? data.data : data;
      
      // Cache the result
      cacheCategoryBlogs(`tag_${tag}`, params, result);
      
      return result;
    } catch (error) {
      console.error('Error fetching blogs by tag:', error);
      throw error;
    }
  },

  // Search blogs
  async searchBlogs(query, params = {}) {
    try {
      // Check cache first
      const cached = getCachedSearchResults(query, params);
      if (cached) {
        return cached;
      }

      const response = await blogApi.get('/blog/search', { 
        params: { q: query, ...params } 
      });
      const data = response.data;
      
      // Extract data from API response format
      const result = data.success ? data.data : data;
      
      // Cache the result
      cacheSearchResults(query, params, result);
      
      return result;
    } catch (error) {
      console.error('Error searching blogs:', error);
      throw error;
    }
  },

  // Get blog categories
  async getCategories() {
    console.log('🔵 blogService.getCategories called');
    try {
      // Check cache first
      const cached = getCachedCategories();
      if (cached) {
        console.log('✅ Returning cached categories:', cached);
        return cached;
      }

      console.log('🌐 Making API request to /blog/categories');
      const response = await blogApi.get('/blog/categories');
      console.log('✅ API response received:', response.status, response.data);
      const data = response.data;
      
      // Extract data from API response format
      const result = data.success ? data.data : data;
      console.log('📦 Extracted result:', result);
      
      // Cache the result
      cacheCategories(result);
      
      return result;
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      console.error('❌ Error details:', error.message, error.response?.status, error.response?.data);
      // Return fallback categories derived from fallback blogs
      const fallbackCategories = [...new Set(fallbackBlogs.map(b => b.category))].map(cat => ({
        name: cat.charAt(0).toUpperCase() + cat.slice(1),
        slug: cat,
        postCount: fallbackBlogs.filter(b => b.category === cat).length,
      }));
      return fallbackCategories;
    }
  },

  // Get featured blogs (first few published blogs)
  async getFeaturedBlogs(limit = 3) {
    console.log('🔵 blogService.getFeaturedBlogs called with limit:', limit);
    try {
      // Check cache first
      const cached = getCachedFeaturedBlogs();
      if (cached) {
        console.log('✅ Returning cached featured blogs:', cached);
        return cached;
      }

      console.log('🌐 Making API request to /blog/posts for featured blogs');
      const response = await blogApi.get('/blog/posts', { 
        params: { limit, featured: true } 
      });
      console.log('✅ API response received:', response.status, response.data);
      const data = response.data;
      
      // Extract data from API response format
      const result = data.success ? data.data : data;
      console.log('📦 Extracted result:', result);
      
      // Cache the result
      cacheFeaturedBlogs(result);
      
      return result;
    } catch (error) {
      console.error('❌ Error fetching featured blogs:', error);
      console.error('❌ Error details:', error.message, error.response?.status, error.response?.data);
      // Return fallback featured blogs
      return { posts: fallbackBlogs.slice(0, limit), total: fallbackBlogs.length };
    }
  },

  // Get related blogs based on category and tags
  async getRelatedBlogs(blogId, limit = 5) {
    try {
      // Check cache first
      const cached = getCachedRelatedBlogs(blogId);
      if (cached) {
        return cached;
      }

      const response = await blogApi.get(`/blog/posts/${blogId}/related`, { 
        params: { limit } 
      });
      const data = response.data;
      
      // Extract data from API response format
      const result = data.success ? data.data : data;
      
      // Cache the result
      cacheRelatedBlogs(blogId, result);
      
      return result;
    } catch (error) {
      console.error('Error fetching related blogs:', error);
      throw error;
    }
  },

  // Cache invalidation methods
  invalidateCache: {
    blogPost: (slug) => invalidateBlogPost(slug),
    blogLists: () => invalidateBlogLists(),
    searchResults: () => invalidateSearchCaches()
  }
};

export default blogService;